// ink-component
import React, { MouseEventHandler, useContext, useEffect, useRef } from 'react';
import { SideNavContext } from '../../SideNavContext';
import getGlobalProps from '../../../../helpers/constants/global-props';
import { BubbleProps } from '../../../Bubble/Bubble';
import { StyledSideNavGroupItemAnchor, StyledSideNavGroupItemLink } from './styles';
import { SideNavGroupItemContent } from './SideNavGroupItemContent';

export interface SideNavGroupItemProps extends React.HTMLAttributes<HTMLAnchorElement> {
  /** Link title text */
  title: string;
  /** Active state modifier */
  isActive: boolean;
  /** Visible state modifier */
  isVisible: boolean;
  /** URL to navigate to */
  url: string;
  /** Bubble type and text */
  bubble?: { type?: BubbleProps['type']; text: string | number } | null;
  /** Open link in new tab */
  openInNewTab?: boolean;
  /** Use React Router Link element instead of native anchor */
  withReactRouter?: boolean;
  /** onClick callback, sends the event */
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
  /** Marks the element with a given ID to make it easier to select it on tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Navigation nested list item element (li)
 * @group SideNav
 * @categories navigation
 * @HTMLType HTMLAnchorElement
 */
const SideNavGroupItem = ({
  title,
  isActive,
  url,
  bubble,
  isVisible,
  openInNewTab,
  withReactRouter = false,
  onClick,
  'data-testid': dataTestId,
}: SideNavGroupItemProps) => {
  const { isClosed } = useContext(SideNavContext);
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const itemElementRef = useRef<HTMLAnchorElement | null>(null);
  const globalProps = getGlobalProps({});

  useEffect(() => {
    if (!isActive || !isVisible) return;
    const itemElement = itemElementRef.current;
    if (itemElement && !isClosed) {
      // @ts-ignore error needed as for some reason TS says 'instant' isn't an option for behaviour even though the docs say otherwise
      itemElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'start' });
    }
  }, [isActive, isClosed, isVisible]);

  return (
    <li {...globalProps} data-testid={dataTestId}>
      {withReactRouter ? (
        <StyledSideNavGroupItemLink
          data-testid={dataTestId ? `${dataTestId}-router-link${isActive ? '--active' : ''}` : ''}
          to={url}
          isClosed={isClosed}
          isVisible={isVisible}
          isActive={isActive}
          // @ts-expect-error
          ref={itemElementRef}
          target={openInNewTab ? '__blank' : undefined}
          rel={openInNewTab ? 'noreferrer noopener' : undefined}
          onClick={onClick}
        >
          <SideNavGroupItemContent title={title} isClosed={isClosed} bubble={bubble} data-testid={dataTestId} />
        </StyledSideNavGroupItemLink>
      ) : (
        <StyledSideNavGroupItemAnchor
          data-testid={dataTestId ? `${dataTestId}-anchor${isActive ? '--active' : ''}` : ''}
          isClosed={isClosed}
          isVisible={isVisible}
          isActive={isActive}
          href={url}
          ref={itemElementRef}
          target={openInNewTab ? '__blank' : undefined}
          rel={openInNewTab ? 'noreferrer noopener' : undefined}
          onClick={onClick}
        >
          <SideNavGroupItemContent title={title} isClosed={isClosed} bubble={bubble} data-testid={dataTestId} />
        </StyledSideNavGroupItemAnchor>
      )}
    </li>
  );
};

SideNavGroupItem.displayName = 'SideNav.GroupItem';

export default SideNavGroupItem;
